import { useState, useCallback } from "react";
import { Tournament, TournamentWsEvent, TournamentMatrix } from "../types/tournament";
import { useWebSocket, WsStatus } from "./useWebSocket";
import { normalizeCode } from "../utils/inviteCode";

const API = import.meta.env.VITE_API_URL || "/api/v1";

async function apiFetch<T>(
  path: string,
  token: string,
  opts: RequestInit = {},
): Promise<T> {
  const res = await fetch(`${API}${path}`, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(opts.headers ?? {}),
    },
  });
  if (!res.ok) {
    let detail = await res.text().catch(() => res.statusText);
    try {
      const parsed = JSON.parse(detail);
      if (parsed.detail) detail = parsed.detail;
    } catch {
      // not json
    }
    throw new Error(detail);
  }
  return res.json();
}

export function useTournament(token: string | null, onGameCreated?: (sessionId: string, inviteCode: string) => void) {
  const [tournament, setTournament] = useState<Tournament | null>(null);
  const [matrix, setMatrix] = useState<TournamentMatrix | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTournamentByCode = useCallback(async (code: string) => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch<Tournament>(`/tournaments/${normalizeCode(code)}`, token);
      setTournament(data);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const createTournament = useCallback(async (name: string, totalGames: number, settings?: Record<string, any>) => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch<Tournament>("/tournaments", token, {
        method: "POST",
        body: JSON.stringify({ name, total_games: totalGames, settings }),
      });
      setTournament(data);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const joinTournament = useCallback(async () => {
    if (!token || !tournament) return;
    setLoading(true);
    setError(null);
    try {
      await apiFetch(`/tournaments/${tournament.id}/join`, token, { method: "POST" });
      // WS will notify us to update
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [token, tournament]);

  const addRealParticipant = useCallback(async (user_id: string) => {
    if (!token || !tournament) return;
    try {
      await apiFetch(`/tournaments/${tournament.id}/players/real`, token, {
        method: "POST",
        body: JSON.stringify({ user_id }),
      });
    } catch (e: any) {
      setError(e.message);
    }
  }, [token, tournament]);

  const addPhantomParticipant = useCallback(async (display_name: string, avatar: string) => {
    if (!token || !tournament) return;
    try {
      await apiFetch(`/tournaments/${tournament.id}/players/phantom`, token, {
        method: "POST",
        body: JSON.stringify({ display_name, avatar }),
      });
    } catch (e: any) {
      setError(e.message);
    }
  }, [token, tournament]);

  const removeParticipant = useCallback(async (participant_id: string) => {
    if (!token || !tournament) return;
    try {
      await apiFetch(`/tournaments/${tournament.id}/players/${participant_id}`, token, {
        method: "DELETE"
      });
    } catch (e: any) {
      setError(e.message);
    }
  }, [token, tournament]);

  const fetchMatrix = useCallback(async () => {
    if (!token || !tournament) return;
    try {
      const data = await apiFetch<TournamentMatrix>(`/tournaments/${tournament.id}/matrix`, token);
      setMatrix(data);
    } catch (e: any) {
      console.error("Failed to fetch matrix", e);
    }
  }, [token, tournament]);

  const finishTournament = useCallback(async () => {
    if (!token || !tournament) return;
    try {
      await apiFetch(`/tournaments/${tournament.id}/finish`, token, { method: "POST" });
    } catch (e: any) {
      setError(e.message);
    }
  }, [token, tournament]);

  const startNextGame = useCallback(async () => {
    if (!token || !tournament) return;
    try {
      await apiFetch(`/tournaments/${tournament.id}/next-game`, token, { method: "POST" });
    } catch (e: any) {
      setError(e.message);
    }
  }, [token, tournament]);

  const handleWsEvent = useCallback((event: TournamentWsEvent) => {
    switch (event.type) {
      case "participant_joined":
        setTournament(prev => prev ? {
          ...prev,
          participants: [...prev.participants, event.participant]
        } : null);
        break;
      case "participant_removed":
        setTournament(prev => prev ? {
          ...prev,
          participants: prev.participants.filter(p => p.id !== event.participant_id)
        } : null);
        break;
      case "game_created":
        setTournament(prev => prev ? {
          ...prev,
          status: "active",
          current_game_index: event.game_index,
        } : null);
        if (onGameCreated) {
          onGameCreated(event.session_id, event.invite_code);
        }
        break;
      case "tournament_finished":
        setTournament(prev => prev ? { ...prev, status: "finished" } : null);
        break;
      case "error":
        setError(event.message);
        break;
    }
  }, [onGameCreated]);

  const { wsStatus } = useWebSocket(
    tournament ? `/ws/tournament/${tournament.id}` : null,
    token,
    handleWsEvent
  );

  return {
    tournament,
    matrix,
    loading,
    error,
    wsStatus: wsStatus as WsStatus,
    fetchTournamentByCode,
    createTournament,
    joinTournament,
    addRealParticipant,
    addPhantomParticipant,
    removeParticipant,
    fetchMatrix,
    startNextGame,
    finishTournament,
    setTournament,
    setMatrix,
  };
}
